import { ApolloClient, InMemoryCache, gql } from "@apollo/client";
import StorageService from "../../util/storage";
import { getOrganisationId } from "../organisations/organisationsSlice";

const client = new ApolloClient({
    uri: `${process.env.REACT_APP_BACKEND_URL}/graphql`,
    cache: new InMemoryCache(),
    headers: {
        Authorization: `${StorageService.getItem("bearer")}`,
    },
});

const SHUFFLE_KEY = "shuffleKey";
const SHUFFLE_EXPIRY = 1 * 60 * 1000; // 5 minutes in milliseconds

// Function to shuffle an array using a shuffle key
const shuffleArrayWithKey = (array, key) => {
    const newArray = [...array]; // Create a shallow copy of the array
    for (let i = newArray.length - 1; i > 0; i--) {
        const j = key[i] % (i + 1);
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
};

// Function to generate a shuffle key
const generateShuffleKey = (length) => {
    const key = [];
    for (let i = 0; i < length; i++) {
        key.push(Math.floor(Math.random() * length));
    }
    return key;
};

// Deep copy function
const deepCopy = (obj) => {
    return JSON.parse(JSON.stringify(obj));
};

export const regenerateShuffleKey = (force = false) => {
    // Get the shuffle key and timestamp from localStorage
    const storedShuffleData = JSON.parse(localStorage.getItem(SHUFFLE_KEY));
    const currentTime = new Date().getTime();

    let shuffleKey;
    if (!force && storedShuffleData && currentTime - storedShuffleData.timestamp < SHUFFLE_EXPIRY) {
        // Use the existing shuffle key if it's not expired
        shuffleKey = storedShuffleData.key;
    } else {
        // Generate a new shuffle key and store it in localStorage
        shuffleKey = generateShuffleKey(100); // Assuming a maximum of 100 answers
        localStorage.setItem(SHUFFLE_KEY, JSON.stringify({ key: shuffleKey, timestamp: currentTime }));
    }

    return shuffleKey;
};

export const fetchCourses = async (userId) => {
    const orgId = await getOrganisationId();

    const query = gql`
        query GetCourses($id: ID!, $orgId: String!) {
            driverCourses(
                filters: { driver: { id: { eq: $id } }, course: { organisation: { orgId: { eq: $orgId } }, publishedAt: { ne: null } } },
                pagination: { limit: 1000 }
            ) {
                data {
                    id
                    attributes {
                        course {
                            data {
                                id
                                attributes {
                                    Title
                                    Summary
                                    Description
                                    Cover {
                                        data {
                                            attributes {
                                                url
                                            }
                                        }
                                    }

                                    categories {
                                        data {
                                            attributes {
                                                name
                                            }
                                        }
                                    }
                                    Lessons {
                                        ... on ComponentCoursesLessons {
                                            id
                                            title
                                            description
                                            preview
                                            timer
                                        }
                                        ... on ComponentCoursesEmbed {
                                            id
                                            title
                                            embedURL
                                            timer
                                        }
                                        ... on ComponentCoursesQuizzes {
                                            id
                                            title
                                        }
                                        ... on Error {
                                            code
                                            message
                                        }
                                    }
                                }
                            }
                        }

                        driver {
                            data {
                                id
                                attributes {
                                    username
                                }
                            }
                        }
                        score
                        isCompleted
                        progress
                        activity
                        started
                    }
                }
            }
        }
    `;
    const { data } = await client.query({
        query,
        variables: { id: userId, orgId: orgId },
    });
    return data.driverCourses.data;
};

export const fetchCourse = async (id) => {
    const query = gql`
        query GetCourse($id: ID!) {
            driverCourse(id: $id) {
                data {
                    id
                    attributes {
                        course {
                            data {
                                id
                                attributes {
                                    Title
                                    Summary
                                    Description
                                    Cover {
                                        data {
                                            attributes {
                                                url
                                            }
                                        }
                                    }

                                    categories {
                                        data {
                                            attributes {
                                                name
                                            }
                                        }
                                    }
                                    Lessons {
                                        ... on ComponentCoursesLessons {
                                            id
                                            title
                                            description
                                            preview
                                            timer
                                        }
                                        ... on ComponentCoursesEmbed {
                                            id
                                            title
                                            embedURL
                                            timer
                                        }
                                        ... on ComponentCoursesFile {
                                            id
                                            title
                                            file {
                                                data {
                                                    id
                                                    attributes {
                                                        name
                                                        url
                                                        previewUrl
                                                    }
                                                }
                                            }
                                            timer
                                        }
                                        ... on ComponentCoursesQuizzes {
                                            id
                                            title
                                            passingThreshhold
                                            maxRetryAttempts
                                            questions {
                                                id
                                                title
                                                type
                                                points
                                                description
                                                hint
                                                randomize
                                                answers {
                                                    id
                                                    Answer
                                                    Correct
                                                }
                                            }
                                        }
                                        ... on Error {
                                            code
                                            message
                                        }
                                    }
                                }
                            }
                        }

                        driver {
                            data {
                                id
                                attributes {
                                    username
                                }
                            }
                        }
                        score
                        isCompleted
                        progress
                        activity
                        started
                    }
                }
            }
        }
    `;
    const { data } = await client.query({
        query,
        variables: { id },
    });

    // Create a deep copy of the course data
    const courseData = deepCopy(data.driverCourse.data);

    const shuffleKey = regenerateShuffleKey();

    // Iterate through the lessons and shuffle the answers of each quiz
    if (courseData?.attributes?.course?.data?.attributes?.Lessons) {
        const lessons = courseData.attributes.course.data.attributes.Lessons.map((lesson) => {
            if (lesson.__typename === "ComponentCoursesQuizzes") {
                return {
                    ...lesson,
                    questions: lesson.questions.map((question) => {
                        return {
                            ...question,
                            answers: question.randomize ? shuffleArrayWithKey(question.answers, shuffleKey): question.answers,
                        }
                    }),
                };
            }
            return lesson;
        });
        courseData.attributes.course.data.attributes.Lessons = lessons;
    }

    return courseData;

    // return data.driverCourse.data;
};

export const saveProgress = async (id, data) => {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/driver-courses/${id}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: StorageService.getItem("bearer"),
        },
        body: JSON.stringify({ data }),
    });

    if (!response.ok) {
        throw new Error(`Error! status: ${response.status}`);
    }

    return response;
};
